// exportParameterContent.js
import * as XLSX from "xlsx";

/**
 * Gathers all unique languages FOR A SINGLE parameter.
 */
const getLanguages = (paramData) => {
  const langSet = new Set();
  const checkObj = (obj) => {
    if (obj) Object.keys(obj).forEach(lang => langSet.add(lang));
  };

  checkObj(paramData.text);
  checkObj(paramData.dynamicText);
  if (paramData.tips) {
    checkObj(paramData.tips.tipsHeader);
    if (paramData.tips.tipsSvgText) {
      checkObj(paramData.tips.tipsSvgText);
    }
  }
  (paramData.additionalSections || []).forEach(section => {
    checkObj(section.heading);
    checkObj(section.description);
    (section.list || []).forEach(item => checkObj(item.text));
  });
  if (paramData.causes) {
    Object.values(paramData.causes).forEach(cause => {
      checkObj(cause.heading);
      checkObj(cause.description);
      (cause.list || []).forEach(item => checkObj(item.text));
    });
  }
  
  const languages = Array.from(langSet);
  return languages.length > 0 ? languages : ["en"]; // Default to 'en'
};

const transformParameterJsonToRows = (json) => {
  const rows = [];
  
  Object.entries(json).forEach(([parameterName, paramData]) => {
    
    const languages = getLanguages(paramData);
    
    // --- THIS IS THE FIX ---
    // This variable tracks if ANY language added rows for this parameter
    let hasContentForParam = false; 
    // --- END OF FIX ---

    languages.forEach((lang) => {
      let rowAddedForLang = false; // This tracks if we add rows for THIS language

      // 1. Add rows for "text"
      if (paramData.text && paramData.text.hasOwnProperty(lang)) {
        rows.push({
          "Parameter Name": parameterName,
          "Section": "text",
          "Language": lang,
          "Text": paramData.text[lang] || "",
        });
        rowAddedForLang = true;
      }

      // 2. Add rows for "dynamicText"
      if (paramData.dynamicText && paramData.dynamicText.hasOwnProperty(lang)) {
        rows.push({
          "Parameter Name": parameterName,
          "Section": "dynamicText",
          "Language": lang,
          "Text": paramData.dynamicText[lang] || "",
        });
        rowAddedForLang = true;
      }

      // 3. Add rows for "tips"
      if (paramData.tips) {
        const tips = paramData.tips;
        const tipHeader = tips.tipsHeader?.[lang];
        const tipsArray = tips.tipsSvgText?.[lang] || [];
        const svgArray = tips.tipsSvg || [];

        if (tipHeader !== undefined || tipsArray.length > 0) {
          if (tipsArray.length === 0) {
            rows.push({
              "Parameter Name": parameterName,
              "Section": "tips",
              "Language": lang,
              "Header": tipHeader || "",
            });
          } else {
            tipsArray.forEach((tipText, index) => {
              rows.push({
                "Parameter Name": parameterName,
                "Section": "tips",
                "Language": lang,
                "Header": tipHeader || "",
                "List_Item_Text": tipText || "",
                "List_Item_Icon": svgArray[index] || "",
              });
            });
          }
          rowAddedForLang = true;
        }
      }

      // 4. Add rows for "causes"
      if (paramData.causes) {
        Object.entries(paramData.causes).forEach(([causeType, causeData]) => {
          const sectionId = `causes-${causeType}`;
          const list = causeData.list || [];

          const baseCauseRow = {
            "Parameter Name": parameterName,
            "Section": sectionId,
            "Language": lang,
            "Header": causeData.heading?.[lang] || "",
            "Text / Description": causeData.description?.[lang] || "",
            "listType": causeData.listType || "",
          };

          if (list.length === 0) {
            rows.push(baseCauseRow);
          } else {
            list.forEach((item) => {
              rows.push({
                ...baseCauseRow,
                "List_Item_Text": item.text?.[lang] || "",
                "List_Item_Icon": item.image || "",
              });
            });
          }
          rowAddedForLang = true; // Mark as added even if only header/desc
        });
      }

      // 5. Add rows for "additionalSections"
      if (paramData.additionalSections) {
        paramData.additionalSections.forEach((section, index) => {
          const sectionId = `additionalSections-${index}`;
          
          const baseSectionRow = {
            "Parameter Name": parameterName,
            "Section": sectionId,
            "Language": lang,
            "Header": section.heading?.[lang] || "",
            "Text / Description": section.description?.[lang] || "",
            "Type": section.type || "",
            "Value_Dependent": section.valueDependent || "",
            "Value_Type": section.valueType || "",
            "descriptionImage": section.descriptionImage || "",
            "showHeadingIcon": section.showHeadingIcon,
            "showListIcons": section.showListIcons,
            "listType": section.listType || "",
          };
          
          const list = section.list || [];
          if (list.length === 0) {
            rows.push(baseSectionRow);
          } else {
            list.forEach((item) => {
              rows.push({
                ...baseSectionRow,
                "List_Item_Text": item.text?.[lang] || "",
                "List_Item_Icon": item.image || "",
              });
            });
          }
          rowAddedForLang = true; // Mark as added even if only header/desc
        });
      }
      
      // --- THIS IS THE FIX ---
      // If rows were added for this language, update the outer variable
      if (rowAddedForLang) {
        hasContentForParam = true;
      }
      // --- END OF FIX ---
    });
    
    // --- THIS IS THE FIX ---
    // This check now uses the correct variable
    if (!hasContentForParam && languages.length > 0) {
    // --- END OF FIX ---
      rows.push({
        "Parameter Name": parameterName,
        "Section": "text",
        "Language": languages[0], // Default to first lang
      });
    }
  });

  return rows;
};

const handleParameterExport = (contentJson) => {
  if (!contentJson) {
    console.log("No content to export");
    return;
  }
  
  try {
    const data = transformParameterJsonToRows(contentJson);
    
    const headers = [
      "Parameter Name", "Section", "Language", "Text", "Header", 
      "Text / Description", "List_Item_Text", "List_Item_Icon", 
      "Type", "Value_Dependent", "Value_Type", 
      "descriptionImage", "showHeadingIcon", "showListIcons", "listType"
    ];

    const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Parameter Content");
    XLSX.writeFile(workbook, "parameter_content.xlsx");

  } catch (error) {
    console.error("Error during export:", error);
  }
};

export default handleParameterExport;